import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useAuth0 } from '../../components/auth/DevAuth0Provider';
import { fetchActivities } from '../../api/activityApi';
import { fetchAndyNotes } from '../../api/notesApi';
import { useClientsData } from '../../contexts/ClientsDataContext';
import { formatTimeStamp } from '../../api/callData';

// Helper function to highlight search text
const highlightText = (text, searchTerm) => {
  if (!searchTerm || !text) return text;
  
  const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  
  return parts.map((part, index) => 
    regex.test(part) ? (
      <span key={index} className="bg-yellow-200 text-yellow-900 px-1 rounded">
        {part}
      </span>
    ) : part
  );
};

// Helper functions for activity display
const getActivityIcon = (type) => {
  switch (type) {
    case 'user_login': return 'mdi:login';
    case 'user_logout': return 'mdi:logout';
    case 'user_created': return 'mdi:account-plus';
    case 'user_updated': return 'mdi:account-edit';
    case 'user_deleted': return 'mdi:account-remove';
    case 'user_role_assigned': return 'mdi:account-key';
    case 'user_role_removed': return 'mdi:account-key-outline';
    case 'customer_created': return 'mdi:domain-plus';
    case 'customer_updated': return 'mdi:domain-edit';
    case 'customer_deleted': return 'mdi:domain-remove';
    case 'organization_member_added': return 'mdi:account-group-outline';
    case 'organization_member_removed': return 'mdi:account-group';
    case 'call_feedback_submitted': return 'mdi:message-text';
    case 'ticket_created': return 'mdi:ticket-outline';
    case 'ticket_updated': return 'mdi:ticket';
    case 'alert_created': return 'mdi:alert-outline';
    case 'zoho_auth_connected': return 'mdi:link';
    case 'zoho_auth_disconnected': return 'mdi:link-off';
    case 'admin_security_setting_changed': return 'mdi:security';
    case 'admin_bootstrap_customer_created': return 'mdi:bootstrap';
    default: return 'mdi:information';
  }
};

const getActivityColor = (type) => {
  switch (type) {
    case 'user_login': return 'text-blue-400';
    case 'user_logout': return 'text-blue-300';
    case 'user_created': return 'text-blue-500';
    case 'user_updated': return 'text-blue-600';
    case 'user_deleted': return 'text-error';
    case 'user_role_assigned': return 'text-purple-400';
    case 'user_role_removed': return 'text-purple-300';
    case 'customer_created': return 'text-green-400';
    case 'customer_updated': return 'text-green-500';
    case 'customer_deleted': return 'text-error';
    case 'organization_member_added': return 'text-emerald-400';
    case 'organization_member_removed': return 'text-emerald-300';
    case 'call_feedback_submitted': return 'text-yellow-400';
    case 'ticket_created': return 'text-orange-400';
    case 'ticket_updated': return 'text-orange-500';
    case 'alert_created': return 'text-error';
    case 'zoho_auth_connected': return 'text-orange-400';
    case 'zoho_auth_disconnected': return 'text-orange-300';
    case 'admin_security_setting_changed': return 'text-cyan-400';
    case 'admin_bootstrap_customer_created': return 'text-teal-400';
    default: return 'text-gray-400';
  }
};

const formatTimestamp = (timestamp) => {
  const now = new Date();
  const activityDate = new Date(timestamp);
  const diff = now - activityDate;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days} day${days > 1 ? 's' : ''} ago`;
  } else if (hours > 0) {
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  } else {
    return 'Less than an hour ago';
  }
};



const formatUserName = (username) => {
  if (!username) return '';
  
  if (username.includes('.')) {
    return username
      .split('.')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }
  
  return username.charAt(0).toUpperCase() + username.slice(1).toLowerCase();
};

const AdminBestMatchesTab = ({ searchQuery, adminCounts }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [activities, setActivities] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activitiesError, setActivitiesError] = useState(null);
  const [notesError, setNotesError] = useState(null);

  // Use the ClientsDataContext for real data
  const { 
    organizations, 
    orgMembers, 
    isLoading: isClientsLoading, 
    error: clientsError 
  } = useClientsData();

  // Load admin data
  useEffect(() => {
    const loadAdminData = async () => {
      setLoading(true);
      setActivitiesError(null);
      setNotesError(null);
      
      // Load activities and notes independently
      try {
        // Load activities
        const activityFilters = {
          limit: 50,
          offset: 0
        };
        if (searchQuery && searchQuery.trim() !== '') {
          activityFilters.search = searchQuery.trim();
        }
        const activityResponse = await fetchActivities(activityFilters, getAccessTokenSilently);
        const activitiesList = activityResponse.activities || [];
        setActivities(activitiesList);
      } catch (error) {
        console.error("Failed to load activities:", error);
        setActivitiesError("Failed to load activities. Please try again later.");
        setActivities([]);
      }
      
      try {
        // Load notes
        const notesResponse = await fetchAndyNotes();
        const notesList = notesResponse.notes || [];
        setNotes(notesList);
      } catch (error) {
        console.error("Failed to load notes:", error);
        setNotesError("Failed to load notes. Please try again later.");
        setNotes([]);
      } finally {
        setLoading(false);
      }
    };
    
    if (searchQuery.trim() !== '') {
      loadAdminData();
    } else {
      setActivities([]);
      setNotes([]);
      setLoading(false);
    }
  }, [searchQuery, getAccessTokenSilently]);

  // Filter data based on search query
  const filteredClients = organizations ? organizations.filter(org => {
    if (!searchQuery) return false;
    
    const searchLower = searchQuery.toLowerCase();
    const searchableFields = [
      org.name,
      org.display_name,
      org.metadata?.customer_id,
      org.metadata?.client_id
    ].filter(Boolean); // Remove undefined values
    
    return searchableFields.some(field => 
      field.toLowerCase().includes(searchLower)
    );
  }) : [];

  const filteredActivities = activities.filter(activity => {
    if (!searchQuery) return false;
    
    const searchLower = searchQuery.toLowerCase();
    const searchableFields = [
      activity.description,
      activity.user,
      activity.customerName,
      activity.email,
      activity.role,
      activity.type
    ].filter(Boolean);
    
    return searchableFields.some(field => 
      field.toString().toLowerCase().includes(searchLower)
    );
  });

  const filteredNotes = notes.filter(note => {
    if (!searchQuery) return false;
    
    const searchLower = searchQuery.toLowerCase();
    return (
      (note.caller_id && note.caller_id.toLowerCase().includes(searchLower)) ||
      (note.note_text && note.note_text.toLowerCase().includes(searchLower))
    );
  });

  // Create best matches (prioritize clients, then activities, then notes)
  const bestMatches = [
    ...filteredClients.slice(0, 3),
    ...filteredActivities.slice(0, 3),
    ...filteredNotes.slice(0, 4)
  ];

  // Only show loading if we're still loading AND have no data at all
  if ((loading || isClientsLoading) && !filteredClients.length && !filteredActivities.length && !filteredNotes.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Icon icon="eos-icons:loading" className="text-4xl text-primary animate-spin mb-4" />
          <p className="text-font-secondary">Searching admin data...</p>
        </div>
      </div>
    );
  }

  // Only show "no results" if ALL data sources have no results AND no errors
  const hasAnyResults = filteredClients.length > 0 || filteredActivities.length > 0 || filteredNotes.length > 0;
  const hasAnyErrors = clientsError || activitiesError || notesError;
  
  if (!hasAnyResults && !hasAnyErrors && !loading && !isClientsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Icon icon="material-symbols:search-off" className="text-6xl text-bgAccent mb-4" />
          <p className="text-font-secondary text-lg">No admin results found for "{searchQuery}"</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 gap-4 w-full min-h-0">
      <div className="flex flex-col w-full gap-4 min-w-0 min-h-0 transition-all duration-300 ease-out">
        
        {/* Clients Section */}
        {clientsError && !filteredClients.length ? (
          <div className="flex flex-col gap-2 p-4 bg-red-900/20 border border-red-700 rounded-lg">
            <div className="flex items-center gap-2">
              <Icon icon="material-symbols:error-outline" className="text-xl text-error" />
              <p className="text-error text-sm">Error loading clients: {clientsError}</p>
            </div>
          </div>
        ) : filteredClients.length > 0 && (
          <div className="flex flex-col gap-3">
            <div className="flex flex-row items-center justify-between gap-2">
              <h3 className="flex flex-row items-center text-lg font-semibold gap-1">
                <Icon icon="fluent:organization-16-filled" className="text-xl text-secondary" />
                Clients
              </h3>
              <span className="text-sm text-font-secondary">{filteredClients.length} displayed</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 w-full gap-4">
              {filteredClients.slice(0, 3).map((org) => (
                <div
                  key={org.id}
                  className="button-container flex flex-col rounded-lg border-2 transition-all duration-200 border-transparent hover:scale-[1.02]"
                >
                  {/* Header */}
                  <div className="flex flex-row justify-between items-center p-4">
                    <div className="flex flex-row gap-3 items-center flex-1">
                      <Icon
                        icon={org.name === 'helios' ? "mdi:administrator-outline" : "octicon:organization-16"}
                        className={`text-2xl ${org.name === 'helios' ? 'text-primary' : 'text-accent'}`}
                      />
                      <div className="flex flex-col">
                                                 <p className="font-semibold text-lg">{org.display_name || org.name}</p>
                        <p className="text-sm text-font-secondary">{orgMembers[org.id]?.length || 0} members</p>
                      </div>
                    </div>
                  </div>

                  <hr className="border-bgAccent" />

                  {/* Logo Section */}
                  <div className="flex items-center justify-center p-4 min-h-[120px] bg-gray-800 bg-opacity-20">
                    {org.branding?.logo_url ? (
                      <img
                        src={org.branding.logo_url}
                        alt={org.display_name || org.name}
                        className="max-w-full max-h-[100px] object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="flex items-center justify-center w-full h-full" style={{display: org.branding?.logo_url ? 'none' : 'flex'}}>
                      <Icon
                        icon={org.name === 'helios' ? "mdi:shield-crown" : "mdi:office-building"}
                        className={`text-6xl ${org.name === 'helios' ? 'text-primary' : 'text-accent'} opacity-50`}
                      />
                    </div>
                  </div>

                  <hr className="border-bgAccent" />

                  {/* Info Section */}
                  <div className="flex flex-col gap-2 p-4">
                    <div className="flex flex-row items-center justify-between w-full gap-1">
                      <p className="text-xs text-font-secondary uppercase tracking-wide">Organization ID</p>
                      <p className="text-sm p-2 bg-bgSecondary card-container rounded font-mono">
                                                   {org.name}
                      </p>
                    </div>
                    <hr className="border-bgAccent" />
                    <div className="flex flex-row items-center justify-between w-full gap-1">
                      <p className="text-xs text-font-secondary uppercase tracking-wide">Default Role</p>
                      <p className="text-sm p-2 bg-bgSecondary card-container rounded">
                        {org.name === 'helios' ? 'helios_admin' : 'client_admin'}
                      </p>
                    </div>
                    {org.metadata?.client_id && (
                      <>
                        <hr className="border-bgAccent" />
                        <div className="flex flex-row items-center justify-between w-full gap-1">
                          <p className="text-xs text-font-secondary uppercase tracking-wide">Client ID</p>
                          <p className="text-sm p-2 bg-bgSecondary card-container rounded font-mono">
                                                         {org.metadata.client_id}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Activities Section */}
        {activitiesError && !filteredActivities.length ? (
          <div className="flex flex-col gap-2 p-4 bg-red-900/20 border border-red-700 rounded-lg">
            <div className="flex items-center gap-2">
              <Icon icon="material-symbols:error-outline" className="text-xl text-error" />
              <p className="text-error text-sm">Error loading activities: {activitiesError}</p>
            </div>
          </div>
        ) : filteredActivities.length > 0 && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-row items-center justify-between gap-2">
              <h3 className="text-lg font-semibold text-font-primary flex items-center gap-2">
                <Icon icon="hugeicons:activity-04" className="text-2xl text-accent" />
                Activities
              </h3>
              <span className="text-sm text-font-secondary">{filteredActivities.length} displayed</span>
            </div>
            <div className="w-full rounded-lg overflow-hidden flex flex-col">
              {/* Desktop Table View (xl and above) */}
              <div className="flex-shrink-0 hidden xl:block w-full">
                <table className="w-full table-auto text-left border-collapse text-sm">
                  <thead>
                    <tr className="text-font-secondary h-[48px] bg-bgSecondary card-container font-semibold">
                      <th className="w-[40px] truncate max-w-[40px] pl-4 pr-2">Type</th>
                      <th className="w-[200px] truncate max-w-[200px] pr-2">Description</th>
                      <th className="w-[120px] truncate max-w-[120px] pr-2">User</th>
                      <th className="w-[100px] truncate max-w-[100px] pr-2">Customer</th>
                      <th className="w-[100px] truncate max-w-[100px] pr-4">Timestamp</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredActivities.slice(0, 3).map((activity) => (
                      <tr
                        key={activity.id}
                        className="h-[56px] border-b border-border text-font-secondary hover:text-primary hover:bg-background hover:button-container cursor-pointer transition-colors ease-out-in duration-300"
                      >
                        <td className="w-[40px] truncate max-w-[40px] pl-4 pr-2">
                          <Icon
                            icon={getActivityIcon(activity.type)}
                            className={`text-2xl ${getActivityColor(activity.type)}`}
                          />
                        </td>
                                               <td className="w-[200px] truncate max-w-[200px] pr-2 text-font-primary">
                         {activity.description}
                       </td>
                       <td className="w-[120px] truncate max-w-[120px] pr-2">
                         {formatUserName(activity.user)}
                       </td>
                       <td className="w-[100px] truncate max-w-[100px] pr-2">
                         {activity.customerName || 'N/A'}
                       </td>
                        <td className="w-[100px] truncate max-w-[100px] pr-4">
                          {formatTimestamp(activity.timestamp)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View (below xl) */}
              <div className="xl:hidden space-y-2 p-2">
                {filteredActivities.slice(0, 3).map((activity) => (
                  <div
                    key={activity.id}
                    className="p-3 rounded-lg cursor-pointer transition-all duration-300 ease-out-in hover:shadow-lg hover:scale-[1.02] bg-bgSecondary card-container border border-bgAccent"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Icon
                          icon={getActivityIcon(activity.type)}
                          className={`text-2xl ${getActivityColor(activity.type)} flex-shrink-0`}
                        />
                        <div className="flex flex-col">
                                                   <span className="font-semibold text-primary text-sm">
                           {activity.description}
                         </span>
                         <span className="text-xs text-font-secondary">
                           {formatUserName(activity.user)}
                         </span>
                       </div>
                     </div>
                     <span className="text-xs text-font-secondary">
                       {formatTimestamp(activity.timestamp)}
                     </span>
                   </div>

                   <div className="flex items-center justify-between mb-2">
                     <span className="text-xs text-font-secondary">
                       {activity.customerName || 'N/A'}
                     </span>
                     {activity.role && (
                       <span className="text-xs text-font-secondary">
                         {activity.role}
                       </span>
                     )}
                   </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Notes Section */}
        {notesError && !filteredNotes.length ? (
          <div className="flex flex-col gap-2 p-4 bg-red-900/20 border border-red-700 rounded-lg">
            <div className="flex items-center gap-2">
              <Icon icon="material-symbols:error-outline" className="text-xl text-error" />
              <p className="text-error text-sm">Error loading notes: {notesError}</p>
            </div>
          </div>
        ) : filteredNotes.length > 0 && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-row items-center justify-between gap-2">
              <h3 className="text-lg font-semibold text-font-primary flex items-center gap-2">
                <Icon icon="material-symbols:note-alt-rounded" className="text-2xl text-accent" />
                Andy Notes
              </h3>
              <span className="text-sm text-font-secondary">{filteredNotes.length} displayed</span>
            </div>
            <div className="w-full h-full flex flex-col rounded-lg overflow-hidden">
              {/* Desktop List View (xl and above) */}
              <div className="hidden xl:block w-full">
                {filteredNotes.slice(0, 3).map((note) => (
                  <div
                    key={note.id}
                    className="flex py-2 text-font-secondary hover:text-font-primary hover:bg-background hover:button-container cursor-pointer transition-colors ease-out-in duration-300 text-sm"
                  >
                    <div className="flex flex-col gap-1 w-[140px] max-w-[140px] text-font-primary flex-shrink-0">
                      <span className="font-semibold text-primary text-sm">{note.caller_id}</span>
                      <span className="text-sm text-font-secondary">{formatTimeStamp(note.timestamp)}</span>
                   </div>
                   <div className="flex-1 min-w-0">
                     <div className="text-sm leading-relaxed">
                       {note.note_text}
                     </div>
                   </div>
                  </div>
                ))}
              </div>

              {/* Mobile Card View (below xl) */}
              <div className="xl:hidden p-2">
                {filteredNotes.slice(0, 3).map((note) => (
                  <div
                    key={note.id}
                    className="p-3 rounded-lg cursor-pointer transition-all duration-300 ease-out-in hover:shadow-lg hover:scale-[1.02] min-h-[60px] flex flex-col mb-2"
                  >
                    <div className="flex items-start justify-between mb-2 flex-shrink-0">
                      <div className="flex items-center gap-2">
                        <div className="flex flex-col">
                                                     <span className="font-semibold text-primary text-base">
                             {note.caller_id}
                           </span>
                           <span className="text-xs text-font-secondary">
                             {formatTimeStamp(note.timestamp)}
                           </span>
                         </div>
                       </div>
                     </div>

                     <div className="mt-2">
                       <p className="text-sm text-font-secondary leading-relaxed">
                         {note.note_text}
                       </p>
                     </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBestMatchesTab;
